import React, { useMemo, useState } from "react";
import {
  ComposedChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { TrendingUp, Info } from "lucide-react";
import { monthlySeries } from "@/lib/insights";
import { formatCompact } from "@/lib/currency";

const RANGES = [
  { id: 3, label: "3 bln" },
  { id: 6, label: "6 bln" },
  { id: 12, label: "12 bln" },
];

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-3 shadow-lg" data-testid="trend-tooltip">
      <p className="mb-1 font-heading text-xs font-bold text-zinc-900">{label}</p>
      <p className="text-[11px] text-emerald-600">Masuk: {formatCompact(row.income)}</p>
      <p className="text-[11px] text-rose-600">Keluar: {formatCompact(row.expense)}</p>
      <p className={`text-[11px] font-semibold ${row.net >= 0 ? "text-zinc-900" : "text-rose-600"}`}>
        {row.net >= 0 ? "Surplus" : "Defisit"}: {formatCompact(Math.abs(row.net))}
      </p>
    </div>
  );
}

export function MonthlyTrends({ transactions }) {
  const [months, setMonths] = useState(3);
  const data = useMemo(() => monthlySeries(transactions, months), [transactions, months]);

  const totals = useMemo(() => {
    const income = data.series.reduce((s, x) => s + x.income, 0);
    const expense = data.series.reduce((s, x) => s + x.expense, 0);
    return { income, expense, net: income - expense };
  }, [data]);

  return (
    <div className="rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow" data-testid="monthly-trends-card">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-zinc-400" />
          <p className="font-heading text-sm font-bold text-zinc-900">Tren Bulanan</p>
        </div>
        <div className="flex gap-1 rounded-full bg-zinc-100 p-1" data-testid="trend-range-toggle">
          {RANGES.map((r) => (
            <button
              key={r.id}
              data-testid={`trend-range-${r.id}`}
              onClick={() => setMonths(r.id)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                months === r.id ? "bg-zinc-900 text-white" : "text-zinc-500"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {data.series.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-200 p-8 text-center" data-testid="trend-empty">
          <p className="text-sm text-zinc-400">Belum ada data transaksi untuk ditampilkan.</p>
        </div>
      ) : (
        <>
          <div className="h-[190px] w-full" data-testid="trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.series} margin={{ top: 6, right: 4, bottom: 0, left: -14 }} barGap={2}>
                <CartesianGrid vertical={false} stroke="#f1f1f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 10, fill: "#a1a1aa" }}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                  tickFormatter={(v) => (v >= 1e6 ? `${Math.round(v / 1e6)}jt` : v >= 1e3 ? `${Math.round(v / 1e3)}rb` : v)}
                />
                <Tooltip content={<TrendTooltip />} cursor={{ fill: "#fafafa" }} />
                <Bar dataKey="income" name="Masuk" fill="#059669" radius={[4, 4, 0, 0]} maxBarSize={22} />
                <Bar dataKey="expense" name="Keluar" fill="#f43f5e" radius={[4, 4, 0, 0]} maxBarSize={22} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2">
            <div className="rounded-2xl bg-emerald-50 p-3 text-center">
              <p className="whitespace-nowrap font-mono text-[13px] font-bold text-emerald-700">{formatCompact(totals.income)}</p>
              <p className="mt-0.5 text-[10px] text-zinc-500">Total masuk</p>
            </div>
            <div className="rounded-2xl bg-rose-50 p-3 text-center">
              <p className="whitespace-nowrap font-mono text-[13px] font-bold text-rose-600">{formatCompact(totals.expense)}</p>
              <p className="mt-0.5 text-[10px] text-zinc-500">Total keluar</p>
            </div>
            <div className="rounded-2xl bg-zinc-50 p-3 text-center">
              <p className={`whitespace-nowrap font-mono text-[13px] font-bold ${totals.net >= 0 ? "text-zinc-900" : "text-rose-600"}`}>
                {totals.net >= 0 ? "" : "-"}{formatCompact(Math.abs(totals.net))}
              </p>
              <p className="mt-0.5 text-[10px] text-zinc-500">{totals.net >= 0 ? "Surplus" : "Defisit"}</p>
            </div>
          </div>

          {data.insufficient && (
            <div className="mt-3 flex items-start gap-2 rounded-2xl bg-zinc-50 p-3" data-testid="trend-insufficient">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-400" />
              <p className="text-xs leading-relaxed text-zinc-500">
                Baru {data.monthsAvailable} bulan data tersedia. Tren {months} bulan akan lengkap seiring Anda mencatat.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
